import React, { useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import SyncIcon from '@mui/icons-material/Sync';
import { useAuthStore, SyncStatus } from '../../stores/authStore';

/**
 * Shows Google Drive sync status when the user is signed in:
 * - Hidden when idle or not signed in
 * - "Syncing to Drive…" while upload is in progress
 * - "Synced to Drive" briefly after a successful sync
 * - "Drive sync failed" on error
 */
export const SaveStatusIndicator: React.FC = () => {
  const { user, syncStatus, syncError } = useAuthStore();
  const [showSynced, setShowSynced] = useState(false);
  const [prevStatus, setPrevStatus] = useState<SyncStatus>('idle');

  useEffect(() => {
    // Show "Synced" briefly after a successful upload
    if (prevStatus === 'syncing' && syncStatus === 'success') {
      setShowSynced(true);
      const timer = setTimeout(() => setShowSynced(false), 2000);
      return () => clearTimeout(timer);
    }
    setPrevStatus(syncStatus);
  }, [syncStatus, prevStatus]);

  // Only visible when the user is signed in to Google Drive
  if (!user) return null;

  if (showSynced) {
    return (
      <Chip
        icon={<CloudDoneIcon />}
        label="Synced to Drive"
        size="small"
        color="success"
        variant="outlined"
        sx={{ transition: 'opacity 300ms', opacity: 1 }}
      />
    );
  }

  switch (syncStatus) {
    case 'syncing':
      return (
        <Chip
          icon={
            <SyncIcon
              sx={{
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            />
          }
          label="Syncing to Drive…"
          size="small"
          color="info"
          variant="outlined"
        />
      );
    case 'error':
      return (
        <Chip
          icon={<CloudOffIcon />}
          label={syncError ? `Drive: ${syncError}` : 'Drive sync failed'}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    default:
      return null;
  }
};
